/* Winterbags: scroll reveal, design selector tabs and holiday countdown. */
(() => {
  /** @type {IntersectionObserver | null} */
  let observer = null;

  function reveal(root = document) {
    const elements = root.querySelectorAll('[data-wb-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      for (const element of elements) element.classList.add('is-visible');
      return;
    }

    observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    for (const element of elements) observer.observe(element);
  }

  class WbDesignSelector extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
      });

      this.select(0);
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    /**
     * @param {number} index
     * @param {boolean} [focus]
     */
    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.classList.toggle('is-active', active);
        const panel = this.panels[i];
        if (panel) {
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        }
      });
      if (focus) this.tabs[index]?.focus();
    }

    /** @param {string} blockId */
    selectBlock(blockId) {
      const index = this.tabs.findIndex((tab) => tab.dataset.blockId === blockId);
      if (index > -1) this.select(index);
    }

    /**
     * @param {KeyboardEvent} event
     * @param {number} index
     */
    onKeydown(event, index) {
      const count = this.tabs.length;
      const keys = {
        ArrowRight: (index + 1) % count,
        ArrowDown: (index + 1) % count,
        ArrowLeft: (index - 1 + count) % count,
        ArrowUp: (index - 1 + count) % count,
        Home: 0,
        End: count - 1,
      };
      if (!(event.key in keys)) return;
      event.preventDefault();
      this.select(keys[event.key], true);
    }
  }

  class WbCountdown extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end || '');
      if (Number.isNaN(end)) return;
      this.end = end;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const remaining = this.end - Date.now();
      if (remaining <= 0) {
        window.clearInterval(this.timer);
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const parts = {
        days: Math.floor(remaining / 86400000),
        hours: Math.floor(remaining / 3600000) % 24,
        minutes: Math.floor(remaining / 60000) % 60,
        seconds: Math.floor(remaining / 1000) % 60,
      };
      for (const [unit, value] of Object.entries(parts)) {
        const slot = this.querySelector(`[data-unit="${unit}"]`);
        if (slot) slot.textContent = String(value).padStart(2, '0');
      }
    }
  }

  if (!customElements.get('wb-design-selector')) customElements.define('wb-design-selector', WbDesignSelector);
  if (!customElements.get('wb-countdown')) customElements.define('wb-countdown', WbCountdown);

  reveal();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) reveal(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const selector = event.target instanceof Element ? event.target.closest('wb-design-selector') : null;
    if (selector instanceof WbDesignSelector) selector.selectBlock(event.detail.blockId);
  });
})();